import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./AdminHeader.css";

// 簡易環境テーマ関数
const getEnvironmentTheme = (environment: string) => {
  switch (environment) {
    case "production":
      return {
        color: "#dc3545",
        name: "本番",
        accentColor: "#dc3545",
        displayName: "本番環境",
      };
    case "staging":
      return {
        color: "#fd7e14",
        name: "ステージング",
        accentColor: "#fd7e14",
        displayName: "ステージング環境",
      };
    case "qa":
      return {
        color: "#0d6efd",
        name: "QA",
        accentColor: "#0d6efd",
        displayName: "QA環境",
      };
    case "development":
      return {
        color: "#20c997",
        name: "開発",
        accentColor: "#20c997",
        displayName: "開発環境",
      };
    case "experiment":
      return {
        color: "#6f42c1",
        name: "実験",
        accentColor: "#6f42c1",
        displayName: "実験環境",
      };
    default:
      return {
        color: "#28a745",
        name: "ローカル",
        accentColor: "#28a745",
        displayName: "ローカル環境",
      };
  }
};

interface AdminHeaderProps {
  environment?: string;
  version?: string;
}

// ナビゲーション項目の型定義
interface NavItem {
  label: string;
  path: string;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({
  environment = "development",
}) => {
  const theme = getEnvironmentTheme(environment);
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // ナビゲーション項目（認証済みユーザーのみ表示）
  const navItems: NavItem[] = user
    ? [
        { label: "ダッシュボード", path: "/admin" },
        { label: "お問い合わせ", path: "/admin/entities/contact" },
        { label: "メンテナンス", path: "/admin/entities/maintenance" },
        { label: "お知らせ", path: "/admin/entities/announce" },
        { label: "強制リロード", path: "/admin/entities/force-reload" },
      ]
    : [];

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    void logout();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActiveRoute = (path: string): boolean => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <header className="admin-header">
      <div className="admin-header-content">
        <div className="admin-header-left">
          {user && (
            <button
              className="admin-mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="メニューを開閉"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path
                    d="M6 18L18 6M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                ) : (
                  <path
                    d="M4 6h16M4 12h16M4 18h16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                )}
              </svg>
            </button>
          )}
          <Link to="/admin" className="admin-header-logo">
            <img
              src="/static/miyako-logo-small.png"
              srcSet="/static/miyako-logo-small.png 1x, /static/miyako-logo-2x.png 2x"
              alt="都建設株式会社"
              className="miyako-logo-image"
            />
            <h1>都建設株式会社 管理者専用ページ</h1>
          </Link>
        </div>
        <div className="admin-header-right">
          <div className="admin-header-info">
            <span
              className="admin-environment-badge"
              data-env={environment}
              style={{
                backgroundColor: theme.accentColor,
                color: "white",
                border: `2px solid ${theme.accentColor}`,
              }}
            >
              {theme.displayName}
            </span>
          </div>
          {user && (
            <div className="admin-user-info">
              <div className="admin-user-profile">
                <div className="admin-user-details">
                  <span className="admin-user-name">
                    {user.name}
                    <span
                      style={{
                        marginLeft: 6,
                        padding: "1px 6px",
                        borderRadius: 4,
                        fontSize: 11,
                        background: user.role === "server_admin" ? "#7c2d12" : "#e5e7eb",
                        color: user.role === "server_admin" ? "#fff" : "#374151",
                      }}
                    >
                      {user.roleLabel}
                    </span>
                  </span>
                  <span className="admin-user-email">{user.email}</span>
                </div>
              </div>
              <Link
                to="/admin/password"
                className="admin-logout-button"
                title="自分のパスワードを変更"
                style={{ textDecoration: "none", marginRight: 8 }}
              >
                パスワード変更
              </Link>
              <button
                onClick={handleLogout}
                className="admin-logout-button"
                title="ログアウト"
              >
                ログアウト
              </button>
            </div>
          )}
        </div>
      </div>

      {/* モバイルメニュー */}
      {user && isMobileMenuOpen && (
        <>
          <nav className="admin-mobile-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-mobile-nav-link ${
                  isActiveRoute(item.path) ? "active" : ""
                }`}
                onClick={closeMobileMenu}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/admin/password"
              className="admin-mobile-nav-link"
              onClick={closeMobileMenu}
            >
              パスワード変更
            </Link>
            <button
              onClick={handleLogout}
              className="admin-mobile-logout-button"
            >
              ログアウト
            </button>
          </nav>
          <div className="admin-mobile-nav-overlay" onClick={closeMobileMenu} />
        </>
      )}
    </header>
  );
};
